import React, { useEffect, useState } from "react";

//material ui imports
import {
  Grid,
  TextField,
  Typography,
  Divider,
  Button,
  Stack,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import SaveIcon from "@mui/icons-material/Save";
import { useTheme } from "@mui/material/styles";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import AssignmentIcon from "@mui/icons-material/Assignment";

//third party imports
import * as Yup from "yup";
import { useFormik } from "formik";
import useAxiosAuth from "../../../../hooks/useAxiosAuth";

const CandidateSpecializationForm = (props) => {
  //Constants
  const { onSave, onCancel, selectedCandidateSpecialization } = props;
  const axiosAuth = useAxiosAuth();
  const theme = useTheme();

  //States
  const [loading, setLoading] = useState(false);
  const [specializations, setSpecializations] = useState([]);
  const [subSpecializations, setSubSpecializations] = useState([]);
  const pageSize = 1000;

  const formik = useFormik({
    initialValues: {
      id: selectedCandidateSpecialization.id,
      candidateId: selectedCandidateSpecialization.candidateId,
      candidateSpecializationMasterId:
        selectedCandidateSpecialization.candidateSpecializationMasterId || "",
      candidateSubSpecializationMasterId:
        selectedCandidateSpecialization.candidateSubSpecializationMasterId ||
        "",
      specializationName:
        selectedCandidateSpecialization.specializationName || "",
      subSpecializationName:
        selectedCandidateSpecialization.subSpecializationName || "",
    },
    validationSchema: Yup.object({
      specializationName: Yup.string().max(
        100,
        "Must be 100 characters or less"
      ),
      subSpecializationName: Yup.string().max(
        100,
        "Must be 100 characters or less"
      ),
    }),

    onSubmit: (values) => {
      setLoading(true);
      if (selectedCandidateSpecialization.id === 0) {
        axiosAuth
          .post("/api/CandidateSpecializations", values)
          .then((response) => {
            if (response.status === 200) {
              onSave();
            } else {
              setLoading(false);
            }
          })
          .catch((error) => {
            console.log(error);
            setLoading(false);
          });
      } else {
        axiosAuth
          .put("/api/CandidateSpecializations", values)
          .then((response) => {
            if (response.status === 200) {
              onSave();
            } else {
              setLoading(false);
            }
          })
          .catch((error) => {
            console.log(error);
            setLoading(false);
          });
      }
    },
  });

  // Load specializations on component mount
  useEffect(() => {
    getSpecializations();
  }, []);

  useEffect(() => {
    if (formik.values.candidateSpecializationMasterId) {
      getSubSpecializations(formik.values.candidateSpecializationMasterId);
    } else {
      setSubSpecializations([]);
    }
  }, [formik.values.candidateSpecializationMasterId]);

  //Services
  const getSpecializations = () => {
    axiosAuth
      .get("/api/CandidateSpecializationMasters/list?skip=0&take=" + pageSize)
      .then((response) => {
        setSpecializations(response.data);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const getSubSpecializations = (specializationId) => {
    axiosAuth
      .get(
        "/api/CandidateSubSpecizalizationMaster/by-candidate-specialization?candidateSpecializationId=" +
          specializationId
      )
      .then((response) => {
        setSubSpecializations(response.data);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  // Handle specialization selection
  const handleSpecializationChange = (event) => {
    const specializationId = event.target.value;

    // Update formik values
    formik.setFieldValue("candidateSpecializationMasterId", specializationId);

    // Find the selected specialization name
    const selectedSpec = specializations.find(
      (spec) => spec.id === specializationId
    );
    if (selectedSpec) {
      formik.setFieldValue("specializationName", selectedSpec.name);
    }

    // Reset sub-specialization field when specialization changes
    formik.setFieldValue("candidateSubSpecializationMasterId", "");
    formik.setFieldValue("subSpecializationName", "");
  };

  // Handle sub-specialization selection
  const handleSubSpecializationChange = (event) => {
    const subSpecializationId = event.target.value;

    // Update formik values
    formik.setFieldValue(
      "candidateSubSpecializationMasterId",
      subSpecializationId
    );

    // Find the selected sub-specialization name
    const selectedSubSpec = subSpecializations.find(
      (subSpec) => subSpec.id === subSpecializationId
    );
    if (selectedSubSpec) {
      formik.setFieldValue("subSpecializationName", selectedSubSpec.name);
    }
  };

  return (
    <>
      <Grid container>
        {selectedCandidateSpecialization.id === 0 ? (
          <Stack direction="row" sx={{ marginX: "auto", alignItems: "center" }}>
            <BusinessCenterIcon />

            <Typography
              color={theme.palette.primary.dark}
              variant="h5"
              textAlign="center"
              sx={{ marginLeft: 2 }}
            >
              Add New Specialization
            </Typography>
          </Stack>
        ) : (
          <Stack direction="row" sx={{ marginX: "auto", alignItems: "center" }}>
            <BusinessCenterIcon />
            <Typography
              color={theme.palette.primary.dark}
              variant="h5"
              textAlign="center"
              sx={{ marginLeft: 2 }}
            >
              Edit Specialization Details
            </Typography>
          </Stack>
        )}
      </Grid>
      <Divider sx={{ marginY: 2 }} />
      <Grid container spacing={2}>
        <Grid item xl={12} lg={12} md={12} sm={12}>
          <form onSubmit={formik.handleSubmit}>
            <FormControl sx={{ marginBottom: 2 }} fullWidth>
              <InputLabel>Select Specialization</InputLabel>
              <Select
                name="candidateSpecializationMasterId"
                label="Select Specialization"
                onChange={handleSpecializationChange}
                value={formik.values.candidateSpecializationMasterId}
                error={
                  formik.touched.candidateSpecializationMasterId &&
                  Boolean(formik.errors.candidateSpecializationMasterId)
                }
              >
                {specializations.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {/* <TextField
              name="specializationName"
              label="Specialization"
              onChange={formik.handleChange}
              value={formik.values.specializationName}
              fullWidth
              error={
                formik.touched.specializationName &&
                Boolean(formik.errors.specializationName)
              }
              helperText={
                formik.touched.specializationName &&
                formik.errors.specializationName
              }
              sx={{ marginBottom: 2 }}
            /> */}
            <FormControl sx={{ marginBottom: 2 }} fullWidth>
              <InputLabel>Select Sub Specialization</InputLabel>
              <Select
                name="candidateSubSpecializationMasterId"
                label="Select Sub Specialization"
                onChange={handleSubSpecializationChange}
                value={formik.values.candidateSubSpecializationMasterId}
                error={
                  formik.touched.candidateSubSpecializationMasterId &&
                  Boolean(formik.errors.candidateSubSpecializationMasterId)
                }
                disabled={!formik.values.candidateSpecializationMasterId}
              >
                {subSpecializations.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {/* <TextField
              name="subSpecializationName"
              label="Sub Specialization"
              onChange={formik.handleChange}
              value={formik.values.subSpecializationName}
              fullWidth
              error={
                formik.touched.subSpecializationName &&
                Boolean(formik.errors.subSpecializationName)
              }
              helperText={
                formik.touched.subSpecializationName &&
                formik.errors.subSpecializationName
              }
            /> */}
            <Divider sx={{ my: 2 }} />
            <Grid>
              <LoadingButton
                variant="contained"
                type="submit"
                loading={loading}
                loadingPosition="end"
                endIcon={<SaveIcon />}
              >
                Submit
              </LoadingButton>
              <Button
                variant="outlined"
                onClick={onCancel}
                sx={{ marginLeft: 2 }}
              >
                Cancel
              </Button>
            </Grid>
          </form>
        </Grid>
      </Grid>
    </>
  );
};

export default CandidateSpecializationForm;
